import api from './api.service';

export type ReceiptType = 'AgainstInvoice' | 'DirectRevenue' | 'AdvanceFromCustomer' | 'OnAccount' | 'Other';
export type ReceiptPaymentMode = 'Cash' | 'Cheque' | 'BankTransfer' | 'Card' | 'Online' | 'Other';

export interface ReceiptVoucherAllocation {
  id?: string;
  sales_invoice_id: string;
  allocated_amount: number;
  /** Advance-from-customer receipt: GL account credited (default = invoice advance_from_customer). */
  credit_account_id?: string | null;
  salesInvoice?: { invoice_number: string; invoice_type?: string };
  creditAccount?: { id: string; account_code: string; account_name: string };
}

export interface ReceiptVoucherDirectLine {
  id?: string;
  account_id: string;
  amount: number;
  description?: string;
  project_id?: string;
  cost_center?: string;
}

export interface ReceiptVoucherDiscountLine {
  id?: string;
  account_id: string;
  amount: number;
  description?: string;
  account?: { id: string; account_code: string; account_name: string };
}

export interface ReceiptVoucherChargeLine {
  id?: string;
  account_id: string;
  amount: number;
  description?: string;
  project_id?: string;
  account?: { id: string; account_code: string; account_name: string };
  project?: { id: string; project_number?: string; project_name?: string };
}

export type ReceiptSettlementAdvanceAdj = {
  receipt_voucher_id?: string;
  advance_sales_invoice_id?: string;
  amount: number;
};

export type ReceiptSettlementCreditNoteAdj = {
  customer_credit_note_id: string;
  amount: number;
};

export type ReceiptSettlementLineInput = {
  sales_invoice_id: string;
  cash_amount: number;
  advance_adjustments?: ReceiptSettlementAdvanceAdj[];
  credit_note_adjustments?: ReceiptSettlementCreditNoteAdj[];
};

export interface ArSettlementContext {
  customer_id: string;
  currency?: string | null;
  invoices: Array<{
    id: string;
    invoice_number: string;
    invoice_date?: string;
    due_date?: string;
    grand_total: number;
    paid_amount: number;
    balance_due: number;
    currency?: string;
    days_overdue?: number;
    payment_status?: string;
    invoice_type?: string;
    advance_adjusted_amount?: number;
    credit_note_adjusted_amount?: number;
  }>;
  advances: Array<{
    id: string;
    source: 'receipt_voucher' | 'advance_invoice';
    receipt_number: string;
    receipt_date?: string;
    amount: number;
    adjusted_amount?: number;
    open_balance: number;
    narration?: string | null;
    payment_mode?: string;
  }>;
  credit_notes: Array<{
    id: string;
    credit_note_number: string;
    credit_note_date?: string;
    total_amount?: number;
    adjusted_amount?: number;
    open_balance: number;
    reason?: string | null;
  }>;
}

export interface ReceiptVoucher {
  id: string;
  company_id: string;
  receipt_number: string;
  receipt_date: string;
  receipt_type: ReceiptType;
  customer_id?: string;
  amount: number;
  currency: string;
  payment_mode: ReceiptPaymentMode;
  receiving_account_id: string;
  reference_number?: string;
  narration?: string;
  received_from_name?: string;
  cheque_number?: string;
  cheque_date?: string;
  bank_name?: string;
  transaction_reference?: string;
  status: 'Draft' | 'Posted' | 'Cancelled';
  journal_voucher_id?: string;
  project_id?: string;
  department_id?: string;
  write_off_amount?: number;
  write_off_account_id?: string | null;
  writeOffAccount?: { id: string; account_code: string; account_name: string };
  settlement_snapshot?:
    | ReceiptSettlementLineInput[]
    | {
        lines?: ReceiptSettlementLineInput[];
        cash_amount?: number;
        discount_total?: number;
        write_off_amount?: number;
        charge_total?: number;
        cash_allocation_total?: number;
        advance_total?: number;
        credit_note_total?: number;
        invoice_coverage_total?: number;
        remarks?: string | null;
      }
    | null;
  settlement_remarks?: string | null;
  posted_at?: string;
  posted_by?: string;
  allocations?: ReceiptVoucherAllocation[];
  directLines?: ReceiptVoucherDirectLine[];
  discountLines?: ReceiptVoucherDiscountLine[];
  chargeLines?: ReceiptVoucherChargeLine[];
  receivingAccount?: { id: string; account_code: string; account_name: string };
}

export interface CreateReceiptVoucherPayload {
  receipt_date: string;
  receipt_type: ReceiptType;
  customer_id?: string;
  amount: number;
  currency?: string;
  payment_mode: ReceiptPaymentMode;
  receiving_account_id: string;
  reference_number?: string;
  narration?: string;
  received_from_name?: string;
  cheque_number?: string;
  cheque_date?: string;
  bank_name?: string;
  transaction_reference?: string;
  project_id?: string;
  branch_id?: string;
  department_id?: string;
  allocations?: { sales_invoice_id: string; allocated_amount: number; credit_account_id?: string }[];
  direct_lines?: { account_id: string; amount: number; description?: string; project_id?: string; cost_center?: string }[];
  discount_lines?: { account_id: string; amount: number; description?: string }[];
  charge_lines?: { account_id: string; amount: number; description?: string; project_id?: string }[];
  write_off_amount?: number;
  write_off_account_id?: string;
  settlement_lines?: ReceiptSettlementLineInput[];
  settlement_remarks?: string;
}

export interface ReceiptVoucherFilters {
  from_date?: string;
  to_date?: string;
  customer_id?: string;
  status?: string;
  receipt_number?: string;
  payment_mode?: string;
  search?: string;
}

/** Normalize settlement_snapshot (array or { lines }) into settlement line inputs. */
export function parseReceiptSettlementSnapshot(
  snapshot: ReceiptVoucher['settlement_snapshot']
): ReceiptSettlementLineInput[] {
  if (!snapshot) return [];
  if (Array.isArray(snapshot)) return snapshot;
  if (snapshot && typeof snapshot === 'object' && Array.isArray(snapshot.lines)) {
    return snapshot.lines;
  }
  return [];
}

class ReceiptVoucherService {
  async list(filters?: ReceiptVoucherFilters): Promise<ReceiptVoucher[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v != null && v !== '') params.append(k, String(v));
      });
    }
    const response = await api.get<ReceiptVoucher[]>(`/finance/receipt-vouchers?${params.toString()}`);
    const data = (response as any)?.data ?? response;
    return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  }

  async getById(id: string): Promise<ReceiptVoucher> {
    const response = await api.get<ReceiptVoucher>(`/finance/receipt-vouchers/${id}`);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async create(payload: CreateReceiptVoucherPayload): Promise<ReceiptVoucher> {
    const response = await api.post<ReceiptVoucher>('/finance/receipt-vouchers', payload);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async update(
    id: string,
    payload: Partial<CreateReceiptVoucherPayload>
  ): Promise<ReceiptVoucher> {
    const response = await api.put<ReceiptVoucher>(`/finance/receipt-vouchers/${id}`, payload);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async post(id: string): Promise<ReceiptVoucher> {
    const response = await api.post<ReceiptVoucher>(`/finance/receipt-vouchers/${id}/post`);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async cancel(id: string): Promise<ReceiptVoucher> {
    const response = await api.post<ReceiptVoucher>(`/finance/receipt-vouchers/${id}/cancel`);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async getArSettlementContext(customerId: string, currency?: string): Promise<ArSettlementContext> {
    const params = new URLSearchParams();
    params.append('customer_id', customerId);
    if (currency) params.append('currency', currency);
    const response = await api.get<ArSettlementContext>(
      `/finance/receipt-vouchers/ar-settlement-context?${params.toString()}`
    );
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async getPrintReceiptData(id: string): Promise<ReceiptPrintData> {
    const response = await api.get<ReceiptPrintData>(`/finance/receipt-vouchers/${id}/print-receipt`);
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }

  async getPrintVoucherData(id: string): Promise<any> {
    const response = await api.get<any>(`/finance/receipt-vouchers/${id}/print-voucher`);
    const data = (response as any)?.data ?? response;
    const raw = data?.data ?? data;
    return raw ?? null;
  }

  async allocateOnAccount(
    id: string,
    allocations: Array<{ sales_invoice_id: string; amount: number }>
  ): Promise<any> {
    const response = await api.post(`/finance/receipt-vouchers/${id}/allocate`, { allocations });
    const data = (response as any)?.data ?? response;
    return data?.data ?? data;
  }
}

export interface ReceiptPrintData {
  receipt_number: string;
  receipt_date: string;
  received_from: string;
  amount: number;
  currency: string;
  payment_mode: string;
  cheque_number?: string;
  cheque_date?: string;
  bank_name?: string;
  transaction_reference?: string;
  narration?: string;
  allocations: { invoice_number: string; allocated_amount: number }[];
  discount_lines?: {
    account_code?: string;
    account_name?: string;
    amount: number;
    description?: string;
  }[];
  discount_total?: number;
  write_off_amount?: number;
  charge_lines?: {
    account_code?: string;
    account_name?: string;
    amount: number;
    description?: string;
  }[];
  charge_total?: number;
}

export const receiptVoucherService = new ReceiptVoucherService();
export default receiptVoucherService;
